import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { set } from '@ember/object';
import { inject as iService } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import { paesiMondoAlfa3, paesiMondoAlfa2 } from 'beebee-base/lib/list_taxes';
import { beebeeAjaxPost, clearAttribute, notifyUserError } from 'beebee-base/lib/utility';
import getConfig from 'beebee-base/lib/get-config';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      component (booking form step: contact personal data + address)
 *   extends:   Component (@glimmer/component)
 *   template:  form-step-dati.hbs
 *   args:      @applicationController, @onNext
 *   uses:      paesiMondoAlfa3/paesiMondoAlfa2 (beebee-base/lib/list_taxes),
 *              beebeeAjaxPost/clearAttribute/notifyUserError (beebee-base/lib/utility),
 *              getConfig (beebee-base/lib/get-config)
 *   siblings:  form-step-modulo.js, form-step-project.js (other wizard steps)
 *   tests:     form-components-test.js, wave16-components-test.js, wave17-components-test.js
 *   gotchas:   completaStraniero() must never run for Italian contacts — it
 *              overwrites the real postal code/fiscal data with placeholder
 *              values; guarded by the isItaly branch in checkRequired().
 *
 * Step in the multi-step public booking wizard: seeds a default address on
 * mount, validates required contact fields (checkRequired), fills placeholder
 * fiscal data for foreign contacts, then posts the contact to
 * `/checkin/savecontact` via saveTask before calling @onNext.
 */
export default class FormStepDatiComponent extends Component {
  @iService session;
  @iService store;
  @iService notify;
  @iService intl;

  @tracked nameError = false;
  @tracked ragSocialeError = false;
  @tracked birthError = false;
  @tracked luogoError = false;
  @tracked mailError = false;
  @tracked mobileError = false;
  @tracked addressError = false;
  @tracked cfError = false;

  get me() {
    return this.session.me;
  }

  get not_required_field() {
    return this.session.option_field?.booking_settings?.checkin_not_required_field || {};
  }

  get showCodFiscale() {
    const firstAddress = this.me?.addresses?.[0];
    if (!firstAddress) return false;
    const country = firstAddress.location_address_country;
    const countryCode = firstAddress.location_address_country_code;
    if (country && country !== 'Italia') return false;
    return country === 'Italia' || ['IT', 'ITA'].includes(countryCode);
  }

  constructor(owner, args) {
    super(owner, args);
    this.checkAddressTask.perform();
  }

  @(task(function* () {
    yield timeout(150);
    const contact = this.me;
    if (!contact?.get('addresses') || contact.get('addresses.length') === 0) {
      const newAddress = this.store.createRecord('address', {
        is_invoice: true,
        location_address_country: 'Italia',
        location_address_country_code: 'IT',
      });
      contact.get('addresses').push(newAddress);
    }
  }).restartable())
  checkAddressTask;

  checkRequired() {
    const contact = this.me;

    this.nameError = false;
    this.ragSocialeError = false;
    this.birthError = false;
    this.luogoError = false;
    this.mailError = false;
    this.mobileError = false;
    this.addressError = false;
    this.cfError = false;

    ['ragione_sociale', 'first_name', 'last_name', 'cod_fiscale', 'luogo_nascita', 'mail', 'mobile'].forEach((field) => {
      set(contact, field, clearAttribute(contact.get(field)));
    });

    let valid = true;
    const notRequired = this.not_required_field;

    if (contact.get('fisica_giuridica')) {
      if (!contact.get('ragione_sociale')) { this.ragSocialeError = true; valid = false; }
    } else {
      if (!contact.get('first_name') || !contact.get('last_name')) { this.nameError = true; valid = false; }
    }

    if (!contact.get('data_nascita') && !notRequired.data_nascita) { this.birthError = true; valid = false; }
    if (!contact.get('luogo_nascita') && !notRequired.luogo_nascita) { this.luogoError = true; valid = false; }
    if (!contact.get('mail')) { this.mailError = true; valid = false; }
    if (!contact.get('mobile') && !notRequired.mobile) { this.mobileError = true; valid = false; }

    const address = contact.get('addresses')?.[0];
    if (
      !(
        address?.location_address_street &&
        address?.location_address_house &&
        address?.location_address_postal_code &&
        address?.location_address_city &&
        address?.location_address_country
      ) &&
      !notRequired.address
    ) {
      this.addressError = true;
      valid = false;
    }

    const country = address?.location_address_country;
    const countryCode = address?.location_address_country_code;
    const isItaly = country === 'Italia' || ['IT', 'ITA'].includes(countryCode);

    if (isItaly) {
      // completaStraniero() must never run for Italian contacts: it would
      // overwrite the real postal code / fiscal data with placeholder values.
      if (
        !notRequired.cod_fiscale &&
        (!contact.get('cod_fiscale') || contact.get('codFiscaleValidMsg'))
      ) {
        this.cfError = true;
        valid = false;
      }
    } else {
      this.completaStraniero();
    }

    return valid;
  }

  completaStraniero() {
    const item = this.me;
    const contactAddress = item.addresses[0];
    const countryCode = contactAddress?.location_address_country_code;
    const contactId = item.id || '';

    if (!countryCode) return;

    const convertToAlpha2 = (code) => {
      const index = paesiMondoAlfa3.indexOf(code);
      if (index === -1) return code.substring(0, 2);
      return paesiMondoAlfa2[index];
    };

    const alpha2Code = convertToAlpha2(countryCode);

    set(contactAddress, 'location_address_postal_code', '00000');
    set(item, 'codice_destinatario', 'XXXXXXX');

    if (item.fisica_giuridica) {
      const rimanentiCF = 14 - contactId.length;
      set(item, 'cod_fiscale', `${alpha2Code}${'00000000000000'.substring(0, rimanentiCF)}${contactId}`);
    } else {
      set(item, 'cod_fiscale', null);
    }

    const rimanentiPIVA = 11 - contactId.length;
    set(item, 'piva', `${alpha2Code}${'999999999'.substring(0, rimanentiPIVA)}${contactId}`);
  }

  createAddressObj() {
    const firstAddress = this.me?.get('addresses')?.[0];
    if (!firstAddress) return null;

    const clean = (value) => (typeof value === 'string' ? value.replace(/'/g, ' ') : value);

    return {
      id: firstAddress.id,
      attributes: {
        id: firstAddress.id,
        name: clean(firstAddress.name),
        icon: firstAddress.icon,
        view: firstAddress.view,
        place_id: firstAddress.place_id,
        location_position_lat: clean(firstAddress.location_position_lat),
        location_position_lng: clean(firstAddress.location_position_lng),
        location_address_text: clean(firstAddress.location_address_text),
        location_address_house: clean(firstAddress.location_address_house),
        location_address_street: clean(firstAddress.location_address_street),
        location_address_postal_code: clean(firstAddress.location_address_postal_code),
        location_address_city: clean(firstAddress.location_address_city),
        location_address_county: clean(firstAddress.location_address_county),
        location_address_state: clean(firstAddress.location_address_state),
        location_address_country: clean(firstAddress.location_address_country),
        location_address_country_code: clean(firstAddress.location_address_country_code),
        categories_id: firstAddress.categories_id,
        categories_title: clean(firstAddress.categories_title),
        type: firstAddress.type,
        is_invoice: firstAddress.is_invoice,
        accuracy: firstAddress.accuracy,
        heading: firstAddress.heading,
      },
    };
  }

  createDataObj() {
    const hashString = (str) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0;
      }
      return Math.abs(hash).toString(16);
    };

    const ragione_sociale = clearAttribute(this.me.get('ragione_sociale')) ?? '';
    const first_name = clearAttribute(this.me.get('first_name')) ?? '';
    const last_name = clearAttribute(this.me.get('last_name')) ?? '';
    const mail = clearAttribute(this.me.get('mail')) ?? '';

    const contact = {
      id: this.me.get('id'),
      gdpr: this.args.applicationController?.get('gdprField.organization_term_options.data'),
      fisica_giuridica: this.me.get('fisica_giuridica'),
      ragione_sociale,
      first_name,
      last_name,
      data_nascita: clearAttribute(this.me.get('data_nascita')),
      luogo_nascita: clearAttribute(this.me.get('luogo_nascita')),
      piva: clearAttribute(this.me.get('piva')),
      cod_fiscale: clearAttribute(this.me.get('cod_fiscale')),
      mail,
      mobile: clearAttribute(this.me.get('mobile')),
      addresses: [],
    };

    if (this.me.get('addresses')?.[0]?.name) {
      contact.addresses.push(this.createAddressObj());
    }

    return {
      contact,
      custom_fields: [],
      validation: hashString(`${mail}|${first_name}|${last_name}|${ragione_sociale}`),
    };
  }

  @(task(function* () {
    if (!this.checkRequired()) {
      this.notify.warning(this.intl.t('missing mandatory fields'));
      return;
    }

    try {
      yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/checkin/savecontact`,
        this.session.token,
        this.createDataObj()
      );

      this.args.onNext();
    } catch (e) {
      notifyUserError(this.notify, e, this.intl);
    }
  }).drop())
  saveTask;

  @action
  setFisicaGiuridica(value) {
    set(this.me, 'fisica_giuridica', value);
  }

  @action
  setDataNascita(value) {
    set(this.me, 'data_nascita', value);
  }

  @action
  setMobile(value) {
    set(this.me, 'mobile', value);
  }

  @action
  next() {
    this.saveTask.perform();
  }
}
